/**
 * Usuwa z SVG wszystko, co potrafi wykonać kod.
 *
 * AT-9147: `svg-block` pobierał plik SVG i przepuszczał go przez
 * `bypassSecurityTrustHtml`, a jedyną „ochroną" był `split('<script>')`. Ten filtr:
 * - łapie wyłącznie dokładny, mały literami `<script>` bez atrybutów,
 * - w ogóle nie rusza atrybutów zdarzeń, więc `<svg onload=alert(1)>` przechodził.
 *
 * SVG-e są treścią CMS najemcy, a wykonują się u każdego odwiedzającego publiczną
 * stronę — to stored XSS.
 *
 * Parsowanie zostawiamy przeglądarce (DOMParser), sami tylko usuwamy niebezpieczne
 * węzły i atrybuty. Funkcja jest bezpieczna do wywołania po stronie serwera: gdy
 * `DOMParser` nie istnieje, zwraca pusty łańcuch zamiast ryzykować przepuszczenie
 * nieprzetworzonej treści.
 */

/** Elementy, które w SVG potrafią wykonać kod albo osadzić obcą treść. */
const FORBIDDEN_ELEMENTS = [
  'script',
  'foreignobject',
  'iframe',
  'embed',
  'object',
  'handler',
  'set',
  'animate',
  'animatetransform',
  'animatemotion',
  'audio',
  'video',
];

/** Atrybuty niosące adres — sprawdzamy w nich schemat. */
const URL_ATTRIBUTES = ['href', 'xlink:href', 'src', 'action', 'formaction', 'from', 'to', 'values'];

/** Schematy, których nie wpuszczamy do atrybutów URL-owych. */
const FORBIDDEN_SCHEMES = ['javascript:', 'vbscript:', 'data:text/html', 'data:image/svg+xml'];

function isDangerousAttribute(name: string, value: string): boolean {
  const lowerName = name.toLowerCase();

  // Każdy atrybut zdarzenia: onload, onclick, onbegin, onmouseover…
  if (lowerName.startsWith('on')) {
    return true;
  }

  if (!URL_ATTRIBUTES.includes(lowerName) && !URL_ATTRIBUTES.includes(lowerName.split(':').pop() || '')) {
    return false;
  }

  // Białe znaki bywają używane do rozbicia schematu ("java\nscript:").
  const normalized = value.replace(/\s+/g, '').toLowerCase();

  return FORBIDDEN_SCHEMES.some(scheme => normalized.startsWith(scheme.replace(/\s/g, '')));
}

/**
 * Zamienia sekcje CDATA na zwykły tekst.
 *
 * `XMLSerializer` zachowuje `<![CDATA[…]]>`, a parser HTML — wstawiając wynik przez `innerHTML`
 * — w treści SVG widzi w tym „zepsuty komentarz" kończący się na pierwszym `>`. Ukryty
 * w CDATA ciąg `</title><script>…` odzyskuje wtedy znaczenie znaczników (różnica między
 * parserem XML, którym sprawdzamy, a HTML, który wykona wynik). Jako zwykły tekst
 * serializator zamienia `<` i `&` na encje, więc znaczniki nie mają jak powstać.
 */
function replaceCdataWithText(root: Node): void {
  Array.from(root.childNodes).forEach(child => {
    if (child.nodeType === 4) {
      child.parentNode?.replaceChild(root.ownerDocument?.createTextNode(child.nodeValue ?? '') ?? child, child);
      return;
    }
    replaceCdataWithText(child);
  });
}

/**
 * Usuwa komentarze (8), instrukcje przetwarzania (7) i deklaracje DOCTYPE (10).
 *
 * AT-9147: dla parsera XML `<!--><img src=x onerror=…>-->` to jeden poprawny komentarz,
 * a dla parsera HTML (`innerHTML`) `<!-->` jest już pustym, zamkniętym komentarzem, więc
 * `<img onerror>` staje się żywym elementem. Tak samo `<?x ><img …>?>` (HTML czyta to jako
 * „bogus comment" kończący się na pierwszym `>`). Żaden z tych węzłów nie niesie grafiki,
 * więc usuwamy je wszystkie, a nie próbujemy ich „naprawiać".
 */
function removeUnsafeNodes(root: Node): void {
  Array.from(root.childNodes).forEach(child => {
    if (child.nodeType === 7 || child.nodeType === 8 || child.nodeType === 10) {
      root.removeChild(child);
      return;
    }
    removeUnsafeNodes(child);
  });
}

/**
 * Ogranicza arkusz stylów osadzony w SVG (AT-9147).
 *
 * `<style>` z SVG wstawionego inline obowiązuje w całym dokumencie, więc bez ograniczeń
 * pozwalałby ładować zasoby z obcych hostów (`url()`, `@import`, `@font-face`) i podmieniać
 * wygląd strony przez zasoby zewnętrzne. Zostawiamy lokalne reguły, `@keyframes` i `@media`
 * oraz odwołania wewnątrz pliku (`url(#gradient)`).
 */
export function restrictStylesheet(css: string): string {
  return css
    // Ucieczki (`\75rl(`) pozwalają rozbić słowo kluczowe tak, że filtr go nie widzi.
    .replace(/\\/g, '')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/@font-face\s*\{[^}]*\}/gi, '')
    .replace(/@(import|namespace|charset)[^;]*;?/gi, '')
    .replace(/url\(\s*(?!['"]?\s*#)[^)]*\)/gi, 'none')
    .replace(/expression\s*\(|-moz-binding|behavior\s*:/gi, '');
}

export function sanitizeSvg(svg: string): string {
  if (!svg || typeof DOMParser === 'undefined') {
    return '';
  }

  const document = new DOMParser().parseFromString(svg, 'image/svg+xml');

  if (document.querySelector('parsererror')) {
    // Nie umiemy tego sparsować, więc nie umiemy tego skontrolować.
    return '';
  }

  replaceCdataWithText(document);
  removeUnsafeNodes(document);

  document.querySelectorAll('*').forEach(element => {
    // Porównujemy `localName`, nie `tagName`: element w obcej przestrzeni nazw z prefiksem
    // (np. `h:script`) ma `tagName` z prefiksem i omijał listę.
    if (FORBIDDEN_ELEMENTS.includes(element.localName.toLowerCase())) {
      element.remove();
      return;
    }

    // Obce zasoby w arkuszu (`@import`, `url(https://…)`, `@font-face`) to eksfiltracja
    // przez selektory atrybutów i podmiana wyglądu strony. Samą grafikę stylowaną
    // lokalnie zostawiamy.
    if (element.localName.toLowerCase() === 'style' && element.textContent) {
      element.textContent = restrictStylesheet(element.textContent);
    }

    // Kopia listy, bo usuwanie w trakcie iteracji gubi elementy.
    Array.from(element.attributes).forEach(attribute => {
      if (isDangerousAttribute(attribute.name, attribute.value)) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return new XMLSerializer().serializeToString(document);
}

/** Dozwolony zapis wymiaru: liczba z opcjonalną jednostką albo `auto`. */
const SAFE_DIMENSION = /^(auto|\d+(\.\d+)?(px|%|em|rem|vh|vw)?)$/;

/**
 * Ustawia wymiary na znaczniku `<svg>` już po sanityzacji.
 *
 * AT-9147: wartości `width`/`height` pochodzą ze stylów bloku (dane najemcy) i były doklejane
 * do znacznika zwykłym `replace`, czyli po sanitizerze. Wartość z cudzysłowem wychodziła
 * z atrybutu i dopisywała atrybut zdarzenia. Przyjmujemy więc tylko zapis liczbowy
 * z jednostką; pozostałe wartości są pomijane tak, jakby ich nie było.
 */
export function applySvgDimensions(
  svg: string,
  dimensions: { width?: unknown; height?: unknown },
): string {
  const accepted = (value: unknown): string | null => {
    const text = typeof value === 'number' ? String(value) : value;

    return typeof text === 'string' && SAFE_DIMENSION.test(text.trim()) ? text.trim() : null;
  };

  const width = accepted(dimensions.width);
  const height = accepted(dimensions.height);

  let result = svg;
  if (width) {
    result = result.replace('<svg', () => `<svg width="${width}"`);
  }
  if (height) {
    result = result.replace('<svg', () => `<svg height="${height}"`);
  }

  return result;
}
